import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import {
  EllipsisMenuCopyItem,
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import {
  showLoadingNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { Box, Tooltip } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  EllipsisMenuAuditEffectsItem,
  EllipsisMenuSourceDownloadItem,
  HiddenFeatureIndicator,
} from '../../../components';
import { useDepositDetailDrawer } from '../../../drawers';
import { useExtractMutation } from '../../../hooks';
import { useJournalEntriesModal } from '../../../modals';
import type { DepositRow } from './useDepositsTableQuery';
import { isOpeningBalanceTransaction } from '@vrplatform/embed';

export const DepositEllipsisMenuItems = ({
  deposit,
  openDeleteModal,
  openUnreconcileModal,
  isDrawer,
  openArchiveModal,
}: {
  deposit: DepositRow;
  openDeleteModal: () => void;
  openUnreconcileModal: () => void;
  openArchiveModal: () => void;
  isDrawer?: boolean;
}) => {
  const goto = useNavigate();
  const { open: openJournalEntriesModal } = useJournalEntriesModal();
  const { open: openQuickDetail } = useDepositDetailDrawer();
  const { mutate, loading: loadingExtract } = useExtractMutation();
  const invalidate = useInvalidateApiQueries(['deposits']);

  const { mutateAsync, isPending: loadingOpeningAdvance } = useApiMutation(
    'put',
    '/transactions/{id}'
  );

  const connectionId = deposit.connection?.id;
  const isSynced = !!connectionId;

  const isArchived = deposit.status === 'inactive';

  const openingBalance = isOpeningBalanceTransaction(deposit.uniqueRef);
  const canDelete =
    (deposit.isCsv || !isSynced) && !deposit.isReconciled && !openingBalance;

  const showDeleteTooltip = deposit.isReconciled || !canDelete;

  const duplicate = () => {
    goto(`/deposits/add`, {
      state: {
        depositId: deposit.id,
      },
    });
  };

  const toggleOpeningAdvance = () => {
    const notificationId = `deposit-opening-balance-${deposit.id}`;
    const nextIsOpeningAdvance = !deposit.isOpeningBalance;

    showLoadingNotification({
      id: notificationId,
    });

    mutateAsync({
      params: {
        path: {
          id: deposit.id,
        },
      },
      body: {
        isOpeningBalance: nextIsOpeningAdvance,
      },
    })
      .then(() => {
        updateSuccessNotification({
          id: notificationId,
          title: nextIsOpeningAdvance
            ? 'Opening balance enabled'
            : 'Opening balance disabled',
          message: nextIsOpeningAdvance
            ? 'This deposit is now marked as opening balance.'
            : 'This deposit is no longer marked as opening balance.',
        });
        invalidate();
      })
      .catch((error) => {
        showApiErrorNotification({
          id: notificationId,
          error,
          title: 'Failed to update opening balance',
          defaultMessage:
            'We failed to update the opening balance status. Please try again and contact support if the issue persists.',
        });
      });
  };

  return (
    <>
      {!isDrawer && (
        <>
          <EllipsisMenuItem
            onClick={() => openQuickDetail(deposit.id)}
            customIcon={<Icon icon={'EyeIcon'} size={16} />}
          >
            Quick view
          </EllipsisMenuItem>
          <EllipsisMenuDivider />
        </>
      )}

      {deposit.isReconciled && (
        <>
          <EllipsisMenuItem
            onClick={openUnreconcileModal}
            customIcon={<Icon icon={'CrossCircleIcon'} size={16} />}
          >
            Unmatch
          </EllipsisMenuItem>
          <EllipsisMenuDivider />
        </>
      )}

      <EllipsisMenuItem
        onClick={() => goto(`/deposit/${deposit.id}/edit`)}
        customIcon={<Icon icon={'Edit3Icon'} size={16} />}
        disabled={!!openingBalance || deposit.lock?.status === 'locked'}
        tooltip={
          openingBalance
            ? 'This is an opening balance and cannot be edited through here'
            : (deposit.lock?.message ?? undefined)
        }
      >
        Edit
      </EllipsisMenuItem>

      {!deposit.isCsv && isSynced && !!connectionId && (
        <EllipsisMenuItem
          customIcon={<Icon icon={'RefreshCwIcon'} size={16} />}
          loading={loadingExtract}
          disabled={!deposit.uniqueRef}
          onClick={() =>
            deposit.uniqueRef &&
            mutate({
              connectionId,
              extractor: 'deposit',
              forceUpdate: true,
              params: {
                uniqueRef: deposit.uniqueRef,
              },
            })
          }
        >
          Refetch
        </EllipsisMenuItem>
      )}

      <EllipsisMenuItem icon="CopyIcon" onClick={duplicate}>
        Duplicate
      </EllipsisMenuItem>

      <EllipsisMenuDivider />

      <EllipsisMenuItem
        customIcon={<Icon icon="ListUnorderedIcon" size={16} />}
        onClick={() =>
          openJournalEntriesModal({
            transactionIds: [deposit.id],
          })
        }
      >
        Journal Entries
      </EllipsisMenuItem>

      <RefreshTransactionsJournalEntries id={deposit.id} />

      <EllipsisMenuItem
        onClick={openArchiveModal}
        customIcon={
          <Icon icon={isArchived ? 'RefreshCwIcon' : 'ArchiveIcon'} size={16} />
        }
      >
        {isArchived ? 'Re-enable' : 'Archive'}
      </EllipsisMenuItem>

      <Tooltip
        label={
          openingBalance
            ? 'This is an opening balance and cannot be deleted through here'
            : deposit.isReconciled
              ? 'Please unmatch the deposit before deleting'
              : 'This action is not available for synced deposits'
        }
        position="left"
        withArrow
        disabled={!showDeleteTooltip}
      >
        <Box>
          <EllipsisMenuDangerItem
            onClick={openDeleteModal}
            disabled={!canDelete}
          >
            Delete
          </EllipsisMenuDangerItem>
        </Box>
      </Tooltip>

      <HiddenFeatureIndicator permission="vrp-admin">
        <EllipsisMenuItem
          customIcon={<Icon icon="CircleDollarIcon" size={16} />}
          onClick={toggleOpeningAdvance}
          loading={loadingOpeningAdvance}
        >
          {deposit.isOpeningBalance
            ? 'Disable as opening balance'
            : 'Use as opening balance'}
        </EllipsisMenuItem>

        <EllipsisMenuAuditEffectsItem entityId={deposit.id} />

        <EllipsisMenuCopyItem value={deposit.id} />

        {isSynced && (
          <EllipsisMenuSourceDownloadItem
            fileName={deposit.uniqueRef || 'deposit'}
            where={{
              transaction: {
                id: { _eq: deposit.id },
              },
            }}
          />
        )}
      </HiddenFeatureIndicator>
    </>
  );
};

export const RefreshTransactionsJournalEntries = ({ id }: { id: string }) => {
  const invalidate = useInvalidateApiQueries(['deposits']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'post',
    '/transactions/journal-entries'
  );

  return (
    <EllipsisMenuItem
      customIcon={<Icon icon="RefreshCwIcon" size={16} />}
      onClick={() => {
        showLoadingNotification({
          id,
        });

        mutateAsync({
          body: {
            ids: [id],
          },
        })
          .then(() => {
            updateSuccessNotification({
              id,
              title: 'Journal entries refreshed',
              message: 'Journal entries have been refreshed successfully',
            });
            invalidate();
          })
          .catch((error: any) => {
            showApiErrorNotification({
              id,
              error,
              title: 'Failed to refresh journal entries',
              defaultMessage:
                'We failed to refresh journal entries. Please try again and contact support if the issue persists.',
            });
          });
      }}
      loading={loading}
    >
      Refresh Journal Entries
    </EllipsisMenuItem>
  );
};
